import { LitElement, css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';

import { debounce } from '../helpers/utils';
import { getMainPage } from '../helpers/page-name';

@customElement('range-slider-component')
export class RangeSliderComponent extends LitElement {

    @property({ reflect: true, type: Boolean })
    disabled: boolean = false;

    @property({ type: Number})
    min: number = 0;

    @property({ type: Number})
    max: number = 100;

    @property({ type: Number})
    step: number = 1;

    @property({ type: String })
    background: string = 'var(--theme-input-border-color)';

    @property({ type: String })
    minString: string = 'Min';

    @property({ type: String })
    maxString: string = 'Max';

    @property({ type: String })
    color: string = 'var(--main-secondary-color)';

    @property({ type: Boolean})
    hideCurrentValue: boolean = false;

    @property({ type: String })
    unit: string = '';

    @state()
    actualMin: number = this.min;

    @state()
    actualMax: number = this.max;

    @state()
    actualMinString: string = this.minString;

    @state()
    actualMaxString: string = this.maxString;

    @state()
    value: number = 0;

    @query('#input')
    input?: HTMLInputElement;

    @query('#valueInput')
    valueInput?: HTMLDivElement;

    @query('#slider')
    slider?: HTMLDivElement;

    @query('#thumb')
    thumb?: HTMLDivElement;

    @query('#currentValue')
    currentValue?: HTMLDivElement;

    firstUpdated() {

        // Reset value if it out of range.
        if (this.value < this.min) this.value = this.min;
        if (this.value > this.max) this.value = this.max;

        this.actualMin = this.min;
        this.actualMax = this.max;

        this.actualMinString = this.minString;
        this.actualMaxString = this.maxString;
    
        if (this.step) {
          const minRemainder = this.min % this.step;
          const maxRemainder = this.max % this.step;
    
          if (minRemainder !== 0) {
            this.min = this.min - minRemainder;
          }
    
          // Set the max to the closest multiple of step higher than the max value provided.
          if (maxRemainder !== 0) {
            this.max = this.max + this.step - maxRemainder;
          }
        }
        if (this.input) {
             this.input.value = this.value.toString();
        }

        // add listener for  click on valueInput set focus to input and make it editable
        this.valueInput?.addEventListener('click', () => {
            this.valueInput?.setAttribute('contenteditable', 'true');
            this.valueInput?.focus();
        });

        // add listener for inner text change on div
        this.valueInput?.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                this.contentEditable = 'false';
                this.textChanged();
            }
        });
    }

    /**
     * Sets the slider value.
     */
    _changeHandler() {
        const value = parseInt(this.input?.value ?? '0', 10);

        this.value = value > this.actualMax
        ? this.actualMax
        : value < this.actualMin
            ? this.actualMin
            : value;
        this.dispatchEvent(new Event('change'));
    }

    private textChanged() {
        debounce(() => this._textChangeHandler(this.valueInput?.innerText ?? '0'), 1000)();
    }

    _textChangeHandler(inputValue: string) {
        const average = (this.actualMax + this.actualMin) / 2;
        const value = parseInt(inputValue ?? average.toString(), 10);

        // check if the value is a number and not NaN and is less than the max and greater than the min
        if (isNaN(value) || value < this.actualMin || value > this.actualMax) {
            getMainPage().errorToastElement.showToastAndThrow('Invalid value');
            return;
        }

        this.value = value > this.actualMax
        ? this.actualMax
        : value < this.actualMin
            ? this.actualMin
            : value;
        this.dispatchEvent(new Event('change'));

        // make sure the slider value is updated
        if (this.input) {
            this.input.value = this.value.toString();
        }
    }

    render() {
        return html`
            <div class="range-container">
                <div class="text-holder">
                    <div class="range-slider-min-value"><i>${this.actualMinString}</i></div>
                    <div id="currentValue" class="range-slider-current-value">
                        <span type="number"  id="valueInput">${this.value}</span>
                        ${this.unit}
                    </div>
                    <div class="range-slider-max-value"><i>${this.actualMaxString}</i></div>
                </div>
                <input
                    id="input"
                    max=${this.max}
                    maxString=${this.maxString}
                    min=${this.min}
                    minString=${this.minString}
                    step=${this.step}
                    type="range"
                    value=${this.value}
                    ?disabled=${this.disabled}
                    @change=${this._changeHandler}
                >
            </div>
        `;
    }

    static get styles() {
        return css`

            .text-holder {
                display: flex;
                justify-content: space-between;
                margin-bottom: 5px;
                margin-top: 10px;
            }

            .range-slider-min-value,
            .range-slider-max-value {
                font-size:0.8rem;
                color: var(--theme-lighter-text-color);
                flex: 2 1 40%;
            }

            .range-slider-max-value {
                text-align: right;
            }

            [contenteditable] {
                outline: none;
                cursor: text;
            }

            .range-slider-current-value {
                font-size: 0.8rem;
                font-weight: bold;
                color: var(--main-primary-color);
                flex: 1 1 20%;
                text-align: center;
                border: 1px solid var(--main-secondary-color);
                padding: 2px;
                border-radius: 5px;
                display: flex;
                align-items: center;
                justify-content: center;
            }
        
            :host([disabled]) {
                pointer-events: none;
            }

            input[type=range] {
            -webkit-appearance: none;
            margin: 0;
            width: 100%;
            margin-bottom: 20px;
            }
            input[type=range]:focus {
            outline: none;
            }
            input[type=range]::-webkit-slider-runnable-track {
            width: 100%;
            height: 5px;
            cursor: pointer;
            animate: 0.2s;
            box-shadow: 0px 0px 0px #000000;
            background: #102C3D;
            border-radius: 45px;
            border: 0px solid #000000;
            }
            input[type=range]::-webkit-slider-thumb {
            box-shadow: 1px 1px 0px #102C3D;
            border: 0px solid #2497E3;
            height: 15px;
            width: 9px;
            border-radius: 3px;
            background: #689D9D;
            cursor: pointer;
            -webkit-appearance: none;
            margin-top: -5px;
            }
            input[type=range]:focus::-webkit-slider-runnable-track {
            background: #102C3D;
            }
            input[type=range]::-moz-range-track {
            width: 100%;
            height: 5px;
            cursor: pointer;
            animate: 0.2s;
            box-shadow: 0px 0px 0px #000000;
            background: #102C3D;
            border-radius: 45px;
            border: 0px solid #000000;
            }
            input[type=range]::-moz-range-thumb {
            box-shadow: 1px 1px 0px #102C3D;
            border: 0px solid #2497E3;
            height: 15px;
            width: 9px;
            border-radius: 3px;
            background: #689D9D;
            cursor: pointer;
            }
            input[type=range]::-ms-track {
            width: 100%;
            height: 5px;
            cursor: pointer;
            animate: 0.2s;
            background: transparent;
            border-color: transparent;
            color: transparent;
            }
            input[type=range]::-ms-fill-lower {
            background: #102C3D;
            border: 0px solid #000000;
            border-radius: 90px;
            box-shadow: 0px 0px 0px #000000;
            }
            input[type=range]::-ms-fill-upper {
            background: #102C3D;
            border: 0px solid #000000;
            border-radius: 90px;
            box-shadow: 0px 0px 0px #000000;
            }
            input[type=range]::-ms-thumb {
            margin-top: 1px;
            box-shadow: 1px 1px 0px #102C3D;
            border: 0px solid #2497E3;
            height: 15px;
            width: 9px;
            border-radius: 3px;
            background: #689D9D;
            cursor: pointer;
            }
            input[type=range]:focus::-ms-fill-lower {
            background: #102C3D;
            }
            input[type=range]:focus::-ms-fill-upper {
            background: #102C3D;
            }


        `;
    }
}
